// The LIST axis (brief §5.2): the same filtered rows, flat, plus the ARCHIVE filter — the projection's
// time-boxed recently-exited window joins the list only when the toggle is on, marked `data-archived`
// and quiet, never mixed silently into the open rows. Each row is a button routing the item id to the
// container (the card page rules; this list carries no verdict control). Wide content scrolls inside
// its own region. Empty results render an honest empty state, never fabricated rows (P-SELF).

import { useState } from 'react';
import { EmptyState, Panel } from '../../../primitives.tsx';
import type { WorkBoardProjection, WorkItemRow } from '../../projections.ts';
import { stateLabel } from '../board-model.ts';
import { WorkMiniBadge } from '../../work-ui.tsx';

const ROW_GRID = 'grid grid-cols-[minmax(14rem,1fr)_8rem_8rem_11rem] items-center gap-3 px-5';

function ListRow({
  item,
  archived,
  onOpen,
}: {
  readonly item: WorkItemRow;
  readonly archived: boolean;
  readonly onOpen: (itemId: string) => void;
}): React.JSX.Element {
  return (
    <li>
      <button
        type="button"
        data-item={item.id}
        data-item-state={item.state}
        {...(archived ? { 'data-archived': 'true' } : {})}
        onClick={() => onOpen(item.id)}
        aria-label={`Open ${item.subjectDisplay}`}
        className={`${ROW_GRID} w-full py-3 text-left transition-colors hover:bg-surface-sunken ${archived ? 'text-text-muted' : ''}`}
      >
        <span className="truncate text-sm font-medium text-text">{item.subjectDisplay}</span>
        <span className="text-sm capitalize text-text-muted">{stateLabel(item.state)}</span>
        <span className="ritona-numeric text-sm text-text">{item.figure ?? '—'}</span>
        <span className="flex items-center">
          {item.needsDecision ? (
            <WorkMiniBadge kind="decide">needs a decision</WorkMiniBadge>
          ) : item.attentionLabel !== null ? (
            <WorkMiniBadge kind="stop">{item.attentionLabel}</WorkMiniBadge>
          ) : (
            <span className="ritona-numeric text-xs text-text-subtle">
              evidence {item.evidenceAsOf}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

function ListHead(): React.JSX.Element {
  return (
    <div className={`${ROW_GRID} border-b border-border py-3`}>
      {['Subject', 'State', 'Figure', 'Gate'].map((label) => (
        <span
          key={label}
          className="ritona-numeric text-xs uppercase tracking-eyebrow text-text-subtle"
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function ArchiveToggle({
  pressed,
  onToggle,
}: {
  readonly pressed: boolean;
  readonly onToggle: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onToggle}
      className={
        'rounded-sm border px-3 py-1.5 text-sm font-medium transition-colors ' +
        (pressed
          ? 'border-border-strong bg-surface-sunken text-text'
          : 'border-border bg-surface text-text-muted hover:border-border-strong')
      }
    >
      Include exited
    </button>
  );
}

export interface BoardListProps {
  readonly board: WorkBoardProjection;
  /** The filtered open-item slice. */
  readonly items: readonly WorkItemRow[];
  readonly onOpenCard: (itemId: string) => void;
}

/** The flat list — the same rows as the board, with the exited archive behind its toggle. */
export function BoardList({ board, items, onOpenCard }: BoardListProps): React.JSX.Element {
  const [includeExited, setIncludeExited] = useState(false);
  const archived = board.exits.flatMap((exit) => exit.recentlyExited);
  const empty = items.length === 0 && (!includeExited || archived.length === 0);
  return (
    <section aria-label="Items list" data-axis-view="list">
      <div className="mb-2 flex items-center justify-end">
        <ArchiveToggle pressed={includeExited} onToggle={() => setIncludeExited((on) => !on)} />
      </div>
      <Panel>
        <div className="overflow-x-auto">
          <div className="min-w-[42rem]">
            <ListHead />
            {empty ? (
              <EmptyState>Nothing matches these filters.</EmptyState>
            ) : (
              <ul aria-label="Work items" className="divide-y divide-border">
                {items.map((item) => (
                  <ListRow key={item.id} item={item} archived={false} onOpen={onOpenCard} />
                ))}
                {includeExited
                  ? archived.map((item) => (
                      <ListRow key={item.id} item={item} archived onOpen={onOpenCard} />
                    ))
                  : null}
              </ul>
            )}
          </div>
        </div>
      </Panel>
    </section>
  );
}
